import catalogJson from '../data/catalog.json';
import type { CatalogData, CreatorRecord, PackRecord, ProfileRecord, SearchState, SortDirection, SortKey } from '../types';
import { downloadCountForPack, downloadCountForProfile } from './downloadCounts';
import { deviceSearchText, normalizeDeviceName } from './deviceNames';

export const catalog = catalogJson as CatalogData;
export const profilesById = new Map(catalog.profiles.map((profile) => [profile.id, profile]));
export const packsById = new Map(catalog.packs.map((pack) => [pack.id, pack]));
export const creatorsBySlug = new Map(catalog.creators.map((creator) => [creator.slug, creator]));
export const creatorsByName = new Map(catalog.creators.map((creator) => [creator.name, creator]));

function isCustomOutputResolution(value: string): boolean {
  return /^custom(?:\s|$)/i.test(value);
}

export const outputResolutionFilterOptions = [
  ...new Set(catalog.resolutions.map((resolution) => (isCustomOutputResolution(resolution) ? 'Custom' : resolution))),
];

export function creatorPath(creator: CreatorRecord | string): string {
  const record = typeof creator === 'string' ? creatorsByName.get(creator) : creator;
  return record ? `/creators/${encodeURIComponent(record.slug)}` : '/search';
}

export const DEFAULT_SEARCH: SearchState = {
  query: '',
  device: '',
  connection: '',
  resolution: '',
  crtEffects: false,
  customCrop: false,
  deepColor: false,
  packId: '',
  sort: 'downloads',
  direction: 'desc',
  page: 1,
  pageSize: 25,
};

const SORT_KEYS = new Set<SortKey>([
  'name',
  'device',
  'connection',
  'outputResolution',
  'creator',
  'updatedAt',
  'downloads',
]);

export function searchStateFromParams(params: URLSearchParams): SearchState {
  const requestedSort = params.get('sort');
  const rawSort = (requestedSort === 'uploadedAt' ? 'updatedAt' : requestedSort) as SortKey | null;
  const rawDirection = params.get('direction') as SortDirection | null;
  const pageSize = Number(params.get('pageSize'));
  const rawPage = Number(params.get('page'));
  return {
    query: params.get('q')?.trim() ?? '',
    device: normalizeDeviceName(params.get('device') ?? ''),
    connection: params.get('connection') ?? '',
    resolution: isCustomOutputResolution(params.get('resolution') ?? '') ? 'Custom' : params.get('resolution') ?? '',
    crtEffects: params.get('crtEffects') === '1',
    customCrop: params.get('customCrop') === '1',
    deepColor: params.get('deepColor') === '1',
    packId: params.get('pack') ?? '',
    sort: rawSort && SORT_KEYS.has(rawSort) ? rawSort : DEFAULT_SEARCH.sort,
    direction: rawDirection === 'asc' || rawDirection === 'desc' ? rawDirection : DEFAULT_SEARCH.direction,
    page: Number.isFinite(rawPage) ? Math.max(1, Math.floor(rawPage)) : 1,
    pageSize: [25, 50, 100].includes(pageSize) ? pageSize : DEFAULT_SEARCH.pageSize,
  };
}

export function paramsFromSearchState(state: SearchState): URLSearchParams {
  const params = new URLSearchParams();
  if (state.query) params.set('q', state.query);
  if (state.device) params.set('device', normalizeDeviceName(state.device));
  if (state.connection) params.set('connection', state.connection);
  if (state.resolution) params.set('resolution', state.resolution);
  if (state.crtEffects) params.set('crtEffects', '1');
  if (state.customCrop) params.set('customCrop', '1');
  if (state.deepColor) params.set('deepColor', '1');
  if (state.packId) params.set('pack', state.packId);
  if (state.sort !== DEFAULT_SEARCH.sort) params.set('sort', state.sort);
  if (state.direction !== DEFAULT_SEARCH.direction) params.set('direction', state.direction);
  if (state.page > 1) params.set('page', String(state.page));
  if (state.pageSize !== DEFAULT_SEARCH.pageSize) params.set('pageSize', String(state.pageSize));
  return params;
}

function normalized(value: string): string {
  return value.toLowerCase().replace(/[_&+./-]+/g, ' ').replace(/\s+/g, ' ').trim();
}

export function filterProfiles(profiles: ProfileRecord[], state: SearchState): ProfileRecord[] {
  const terms = normalized(state.query).split(' ').filter(Boolean);
  const device = normalizeDeviceName(state.device);
  return profiles.filter((profile) => {
    if (device && profile.device !== device) return false;
    if (state.connection && profile.connection !== state.connection) return false;
    if (
      state.resolution
      && (state.resolution === 'Custom'
        ? !isCustomOutputResolution(profile.outputResolution)
        : profile.outputResolution !== state.resolution)
    ) return false;
    if (state.crtEffects && !profile.features.crtEffects) return false;
    if (state.customCrop && !profile.features.customCrop) return false;
    if (state.deepColor && !profile.features.deepColor) return false;
    if (state.packId && profile.packId !== state.packId) return false;
    if (terms.length === 0) return true;
    const haystack = normalized(
      [
        profile.name,
        deviceSearchText(profile.device),
        profile.connection,
        profile.inputLabel,
        profile.outputResolution,
        profile.creator,
        profile.packName ?? '',
        profile.collectionPath ?? '',
      ].join(' '),
    );
    return terms.every((term) => haystack.includes(term));
  });
}

function comparisonValue(profile: ProfileRecord, sort: SortKey): string | number {
  if (sort === 'downloads') return downloadCountForProfile(profile);
  if (sort === 'updatedAt') return Date.parse(profile.updatedAt);
  if (sort === 'outputResolution') return outputResolutionRank(profile.outputResolution);
  return profile[sort];
}

function outputResolutionRank(value: string): number {
  if (/^custom/i.test(value)) return 9_000_000 + (Number(value.match(/\d+/)?.[0]) || 0);
  if (/^unspecified$/i.test(value)) return 9_999_999;
  const vertical = /^4k/i.test(value) ? 2160 : Number(value.match(/^(\d+)/)?.[1]) || 0;
  const refresh = Number(value.match(/(?:p|k)(\d+)$/i)?.[1]) || 0;
  return vertical * 1_000 + refresh;
}

export function sortProfiles(profiles: ProfileRecord[], sort: SortKey, direction: SortDirection): ProfileRecord[] {
  const multiplier = direction === 'asc' ? 1 : -1;
  const values = new Map(profiles.map((profile) => [profile.id, comparisonValue(profile, sort)]));
  return [...profiles].sort((left, right) => {
    if (sort === 'outputResolution') {
      const leftNonstandard = /^(custom|unspecified)/i.test(left.outputResolution);
      const rightNonstandard = /^(custom|unspecified)/i.test(right.outputResolution);
      if (leftNonstandard !== rightNonstandard) return leftNonstandard ? 1 : -1;
    }
    const leftValue = values.get(left.id) ?? '';
    const rightValue = values.get(right.id) ?? '';
    const primary =
      typeof leftValue === 'number' && typeof rightValue === 'number'
        ? leftValue - rightValue
        : String(leftValue).localeCompare(String(rightValue), undefined, { numeric: true, sensitivity: 'base' });
    return primary !== 0 ? primary * multiplier : left.name.localeCompare(right.name, undefined, { numeric: true });
  });
}

export function topProfiles(limit = 10, device = ''): ProfileRecord[] {
  const fullDeviceName = normalizeDeviceName(device);
  const profiles = fullDeviceName ? catalog.profiles.filter((profile) => profile.device === fullDeviceName) : catalog.profiles;
  return sortProfiles(profiles, 'downloads', 'desc').slice(0, limit);
}

export function newestProfiles(limit = 10): ProfileRecord[] {
  return [...catalog.profiles]
    .sort(
      (left, right) =>
        Date.parse(right.uploadedAt) - Date.parse(left.uploadedAt)
        || left.name.localeCompare(right.name, undefined, { numeric: true }),
    )
    .slice(0, limit);
}

export function topPacks(limit = 10): PackRecord[] {
  return [...catalog.packs]
    .filter((pack) => pack.profileCount > 1)
    .sort((left, right) => downloadCountForPack(right) - downloadCountForPack(left) || right.profileCount - left.profileCount)
    .slice(0, limit);
}

export function profilePack(profile: ProfileRecord): PackRecord | null {
  return profile.packId ? packsById.get(profile.packId) ?? null : null;
}

export function profilesForPack(pack: PackRecord): ProfileRecord[] {
  return pack.profileIds.map((id) => profilesById.get(id)).filter((profile): profile is ProfileRecord => Boolean(profile));
}

export function profilesForCreator(creator: CreatorRecord): ProfileRecord[] {
  return creator.profileIds.map((id) => profilesById.get(id)).filter((profile): profile is ProfileRecord => Boolean(profile));
}

export function packsForCreator(creator: CreatorRecord): PackRecord[] {
  return creator.packIds.map((id) => packsById.get(id)).filter((pack): pack is PackRecord => Boolean(pack));
}
